import { Component, OnInit, inject, output, input, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { addIcons } from 'ionicons';
import { logoGoogle } from 'ionicons/icons';
import { GoogleAuthService } from '../../shared/services/google-auth.service';
import { PlatformService } from '../../shared/services/platform.service';
import { AuthService } from '../../shared/services/auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { SwNavigatorService } from '../../shared/services/sw-navigator.service';
import { InputComponent2 } from '../shared/input2/input';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { TranslationService } from '../../shared/services/translation.service';
import { ScreenLayer } from '../../shared/enums/screen-layer.enum';
import { PopoverController } from '@ionic/angular';
import { PopUpComponent } from '../shared/pop-up/pop-up.component';
import type { PopupModel } from '../../types/popup-model';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputComponent2, SpinnerComponent, TranslatePipe],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  private googleAuth = inject(GoogleAuthService);
  private platform = inject(PlatformService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private swnav = inject(SwNavigatorService);
  private popoverCtrl = inject(PopoverController);
  private t = inject(TranslationService);

  @ViewChild('googleSigninButton') googleSigninButtonRef!: ElementRef<HTMLElement>;

  // Input for custom intro message
  introMessage = input<string>(this.t.instant('login.introMessage'));

  // Output event when login is successful
  loginSuccess = output<void>();
  // Output event when signup is clicked
  signupClick = output<void>();

  // UI state
  googleButtonInitialized = false;
  isLoggingIn = false;
  isGoogleButtonActive = false;
  isGoogleLoading = false;

  // Login form
  loginForm: FormGroup = this.fb.group({
    emailOrUsername: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(6)]]
  });

  constructor() {
    addIcons({ logoGoogle });
  }

  ngOnInit(): void {
    // Initialize Google Sign-In (callback setup)
    this.initializeGoogleSignIn();
  }

  ngAfterViewInit(): void {
    // Render button after view is ready (ViewChild is available)
    // The actual rendering happens inside initializeGoogleSignIn via setTimeout
  }

  async onLogin(): Promise<void> {
    if (this.loginForm.invalid || this.isLoggingIn) {
      return;
    }

    this.isLoggingIn = true;
    const { emailOrUsername, password } = this.loginForm.value;

    try {
      await this.authService.login(emailOrUsername, password);

      // Trigger storage event for navigation update
      this.platform.dispatchWindowEvent(new StorageEvent('storage', {
        key: 'remateriar_user',
        newValue: JSON.stringify(this.authService.user()),
        storageArea: this.platform.localStorage
      }));

      this.toastService.presentToastSucess(this.t.instant('login.loginSuccess'));
      this.loginForm.reset();

      // Emit success event
      this.loginSuccess.emit();
    } catch (error: any) {
      console.error('Login error:', error);
      this.toastService.presentErrorFromResponse(error, this.t.instant('login.loginError'));
    } finally {
      this.isLoggingIn = false;
    }
  }

  onGoogleSignIn(): void {
    // Trigger the hidden Google button
    console.log('onGoogleSignIn');

    // Set active state for visual feedback
    this.isGoogleButtonActive = true;

    // Use ViewChild reference instead of global document.querySelector
    // This works inside Ionic modals (shadow DOM) where document.querySelector fails
    const container = this.googleSigninButtonRef?.nativeElement;
    const googleButton = container?.querySelector<HTMLElement>('div[role="button"]');
    if (googleButton) {
      googleButton.click();
    } else {
      console.warn('Google Sign-In button not found in container, trying document fallback');
      const fallback = this.platform.querySelector<HTMLElement>('#google-signin-button > div[role="button"]');
      if (fallback) {
        fallback.click();
      } else {
        // In modals on deployed versions, renderButton may not have completed.
        // Re-render and retry, or fall back to Google One Tap prompt.
        console.warn('Google button fallback also failed, attempting re-render + prompt');
        this.retryRenderAndClick();
      }
    }

    // Reset active state after a short delay
    setTimeout(() => {
      this.isGoogleButtonActive = false;
    }, 300);
  }

  /**
   * Re-render the Google button and retry clicking it.
   * Falls back to google.accounts.id.prompt() if rendering still fails.
   */
  private retryRenderAndClick(): void {
    const el = this.googleSigninButtonRef?.nativeElement;
    if (el) {
      this.googleAuth.renderButton(el);
      // Wait for Google to actually inject the button into the DOM
      let attempts = 0;
      const tryClick = () => {
        const btn = el.querySelector<HTMLElement>('div[role="button"]');
        if (btn) {
          btn.click();
        } else if (attempts < 10) {
          attempts++;
          setTimeout(tryClick, 150);
        } else {
          console.warn('Re-render failed, falling back to Google One Tap prompt');
          this.googleAuth.prompt();
        }
      };
      setTimeout(tryClick, 200);
    } else {
      // No container at all – use prompt as last resort
      this.googleAuth.prompt();
    }
  }

  goToSignup(): void {
    this.signupClick.emit();
    this.swnav.navigateObj({ path: '/signup', layer: ScreenLayer.Auth, typeTransition: 1 });
  }

  /**
   * Retry rendering the Google Sign-In button until the ViewChild element
   * is available and the DOM is ready. This handles Ionic modal animation
   * delays that occur in deployed builds but not on localhost.
   */
  private renderGoogleButtonWithRetry(attempt = 0): void {
    const maxAttempts = 15; // Up to ~3 seconds total
    const el = this.googleSigninButtonRef?.nativeElement;

    if (el && el.offsetParent !== null) {
      // Element exists and is visible in the DOM
      this.googleAuth.renderButton(el);
      this.googleButtonInitialized = true;
      console.log('Google button rendered on attempt', attempt);
    } else if (el) {
      // Element exists but might not be visible yet (modal animating)
      this.googleAuth.renderButton(el);
      this.googleButtonInitialized = true;
      console.log('Google button rendered (element not yet visible) on attempt', attempt);
    } else if (attempt < maxAttempts) {
      // ViewChild not available yet, retry
      setTimeout(() => this.renderGoogleButtonWithRetry(attempt + 1), 200);
    } else {
      // Final fallback: try getElementById
      console.warn('ViewChild never became available, falling back to getElementById');
      this.googleAuth.renderButton('google-signin-button');
      this.googleButtonInitialized = true;
    }
  }

  /**
   * Initialize Google Sign-In
   */
  private async initializeGoogleSignIn(): Promise<void> {
    try {
      console.log('Initializing Google Sign-In...');

      // Initialize Google Identity Services with callback
      await this.googleAuth.initialize(async (idToken: string) => {
        console.log('ID token received from Google');

        try {
          // Send id_token to backend for validation
          this.isGoogleLoading = true;
          const response = await this.authService.validateIdToken(idToken);

          // Show welcome popup if new user
          if (response.isNewUser) {
            const popupInfo: PopupModel = {
              titleh2: this.t.instant('login.welcomeTitle'),
              bodyText: this.t.instant('login.welcomeBody'),
              btn1: this.t.instant('login.welcomeButton'),
              // icon: 'remateriar.svg',
              iconSize: 2,
              bannerColor: '#e6f9ec',
              behavior: 2
            };
            const popover = await this.popoverCtrl.create({
              component: PopUpComponent,
              componentProps: { popupInfo },
              cssClass: 'welcome-popup',
              showBackdrop: true,
              backdropDismiss: true,
            });
            await popover.present();
          }

          // Trigger storage event for navigation update
          this.platform.dispatchWindowEvent(new StorageEvent('storage', {
            key: 'remateriar_user',
            newValue: JSON.stringify(response.user),
            storageArea: this.platform.localStorage
          }));

          this.toastService.presentToastSucess(this.t.instant('login.loginSuccess'));
          console.log('Login successful:', response.user);
          // Emit success event
          this.loginSuccess.emit();
        } catch (error) {
          console.error('Failed to validate ID token:', error);
          this.toastService.presentErrorFromResponse(error, this.t.instant('login.googleLoginError'));
        } finally {
          this.isGoogleLoading = false;
        }
      });

      // Render hidden Google Sign-In button using ViewChild reference
      // This works inside Ionic modals where getElementById can't find shadow DOM elements
      // Use a retry mechanism instead of a fixed timeout — in modals (especially deployed),
      // the DOM may not be ready within 100ms due to animations/rendering differences.
      this.renderGoogleButtonWithRetry();

    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
    }
  }
}
